'use client';

import { forwardRef } from 'react';
import { Select as MantineSelect } from '@mantine/core';
import type { SelectProps as MantineSelectProps } from '@mantine/core';
import { SELECT_TRANSITION_PROPS } from './constants';

import type { LockedProps } from '../types';

export type SelectProps = Omit<MantineSelectProps, LockedProps>;

/**
 * ### Select
 *
 * @description
 * Mantine's Select minus per-call-site style overrides (see {@link LockedProps}).
 * Dropdown transitions are applied by default and can be overridden via `comboboxProps`.
 * For the v6-compat `creatable` flow use `CreatableSelect`, and for content-width inline
 * selects use `InlineSelect` — both in `@highlight-cp/core-ui-compositions`.
 *
 * @example
 * <Select data={[{ value: 'a', label: 'A' }]} />
 *
 * @param props - {@link SelectProps}
 * @docs {@link https://mantine.dev/core/select/}
 */
export const Select = forwardRef<HTMLInputElement, SelectProps>(
  ({ comboboxProps, ...rest }, ref) => (
    <MantineSelect
      ref={ref}
      {...rest}
      inputWrapperOrder={['label', 'input', 'error', 'description']}
      comboboxProps={{ transitionProps: SELECT_TRANSITION_PROPS, ...comboboxProps }}
    />
  ),
);
Select.displayName = 'Select';
